import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { BusinessProvider } from './contexts/BusinessContext';
import { PermissionsProvider } from './contexts/PermissionsContext';
import ProtectedRoute from './components/ProtectedRoute';
import RequireRole from './components/RequireRole';
import RequireAdmin from './routes/RequireAdmin';
// Layouts
import ResponsiveAppShell from './layouts/ResponsiveAppShell';
import ResponsiveAdminLayout from './layouts/ResponsiveAdminLayout';

// Components
import RoleBasedDashboardRedirect from './components/RoleBasedDashboardRedirect';

// Auth Pages
import RoleBasedLogin from './pages/RoleBasedLogin';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import BusinessSelector from './pages/BusinessSelector';

// Core Pages
import OwnerDashboard from './pages/dashboards/OwnerDashboard';
import StaffDashboard from './pages/dashboards/StaffDashboard';
import Placeholder from './pages/Placeholder';

// Admin Pages
import AdminDashboard from './pages/admin/AdminDashboard';
import Businesses from './pages/admin/Businesses';
import BusinessesActive from './pages/admin/BusinessesActive';
import BusinessesInactive from './pages/admin/BusinessesInactive';
import BusinessDetails from './pages/admin/BusinessDetails';
import Users from './pages/admin/Users';
import UserDetails from './pages/admin/UserDetails';
import Plans from './pages/admin/Plans';
import PlansManagementV2 from './pages/admin/PlansManagementV2';
import IndustryMenuEditor from './pages/admin/IndustryMenuEditor';
import Subscriptions from './pages/admin/Subscriptions';
import Invoices from './pages/admin/Invoices';
import SystemConfig from './pages/admin/SystemConfig';
import FeatureFlags from './pages/admin/FeatureFlags';
import BusinessLimits from './pages/admin/BusinessLimits';
import Integrations from './pages/admin/Integrations';
import Providers from './pages/admin/Providers';
import SystemLogs from './pages/admin/SystemLogs';
import ErrorTracker from './pages/admin/ErrorTracker';
import AuditLogs from './pages/admin/AuditLogs';
import ServerHealth from './pages/admin/ServerHealth';
import SupportTickets from './pages/admin/SupportTickets';
import Feedback from './pages/admin/Feedback';
import Announcements from './pages/admin/Announcements';
import PlaceholderPage from './pages/admin/PlaceholderPage';

// Part 2 Pages
import PartiesList from './pages/Parties/PartiesList';
import CustomersPage from './pages/Customers/CustomersPage';
import CustomerDetailPage from './pages/Customers/CustomerDetailPage';
import SuppliersPage from './pages/Suppliers/SuppliersPage';
import SupplierDetailPage from './pages/Suppliers/SupplierDetailPage';
import ItemsList from './pages/Items/ItemsList';
import CustomFieldsManager from './pages/Items/CustomFieldsManager';
import QuotationsList from './pages/Quotations/QuotationsList';
import CreateQuotation from './pages/Quotations/CreateQuotation';
import QuotationDetailPage from './pages/Quotations/QuotationDetailPage';
import QuotationPrintPreview from './pages/Quotations/QuotationPrintPreview';
import PrintTemplateSettings from './pages/Settings/PrintTemplateSettings';
import TemplateStudioPage from './pages/Settings/TemplateStudioPage';
import UsersRolesPage from './pages/Settings/UsersRolesPage';
import ChangePasswordPage from './pages/Settings/ChangePasswordPage';
import MasterPasswordPage from './pages/Settings/MasterPasswordPage';
import BusinessSettingsPage from './pages/Settings/BusinessSettingsPage';
import SalesOrdersPage from './pages/SalesOrders/SalesOrdersPage';
import CreateSalesOrderPage from './pages/SalesOrders/CreateSalesOrderPage';
import SalesOrderDetailPage from './pages/SalesOrders/SalesOrderDetailPage';
import ProformaInvoicesPage from './pages/ProformaInvoices/ProformaInvoicesPage';
import CreateProformaInvoice from './pages/ProformaInvoices/CreateProformaInvoice';
import ProformaInvoiceDetailPage from './pages/ProformaInvoices/ProformaInvoiceDetailPage';
import DeliveryChallansPage from './pages/DeliveryChallans/DeliveryChallansPage';
import CreateDeliveryChallanPage from './pages/DeliveryChallans/CreateDeliveryChallanPage';
import DeliveryChallanDetailPage from './pages/DeliveryChallans/DeliveryChallanDetailPage';
import SalesReturnsPage from './pages/SalesReturns/SalesReturnsPage';

// Part 3 Pages - Tracking (Owner)
import LiveTrackingPage from './pages/tracking/LiveTrackingPage';
import StaffRoutesPage from './pages/tracking/StaffRoutesPage';
import VisitsTasksPage from './pages/tracking/VisitsTasksPage';
import TrackingAlertsPage from './pages/tracking/TrackingAlertsPage';
import TrackingReportsPage from './pages/tracking/TrackingReportsPage';
// Part 3 Pages - Tracking (Staff)
import MyShiftPage from './pages/tracking/MyShiftPage';
import MyVisitsPage from './pages/tracking/MyVisitsPage';
// Part 3 NEW Staff Pages
import MyAttendancePage from './pages/Staff/MyAttendancePage';
import MyRoutesPage from './pages/Staff/MyRoutesPage';
import MyProfilePage from './pages/Staff/MyProfilePage';
import MyCollectionsPage from './pages/Staff/MyCollectionsPage';
import MyRetailersMapPage from './pages/Staff/MyRetailersMapPage';
import RecordPlacementPage from './pages/Staff/RecordPlacementPage';
// Part 4 Pages - Collections
import StaffCollectionsProfilePage from './pages/tracking/StaffCollectionsProfilePage';
import MyRetailersPage from './pages/tracking/MyRetailersPage';
// Part 5 Pages - Retailer Maps & Profiles
import RetailerProfilePage from './pages/tracking/RetailerProfilePage';

// Part 5A - Intelligence Pages (Owner)
import OwnerIntelligenceDashboard from './pages/intelligence/OwnerIntelligenceDashboard';
import StaffInsightsPage from './pages/intelligence/StaffInsightsPage';
import StaffDetailPage from './pages/intelligence/StaffDetailPage';
import RetailerRiskPage from './pages/intelligence/RetailerRiskPage';
import AlertsCenterPage from './pages/intelligence/AlertsCenterPage';

// Part 5A - Intelligence Pages (Staff)
import MyPerformancePage from './pages/intelligence/MyPerformancePage';
import MyAlertsPage from './pages/intelligence/MyAlertsPage';
// Staff Performance Page (New Premium version)
import StaffMyPerformancePage from './pages/Staff/MyPerformancePage';

// Part 5B - Billing Pages (Owner)
import BillingDashboard from './pages/billing/BillingDashboard';
import PlansPage from './pages/billing/PlansPage';
import InvoicesPage from './pages/billing/InvoicesPage';

// Part 5B - Admin Billing Pages
import AdminBillingOverview from './pages/admin/billing/AdminBillingOverview';
import AdminSubscriptions from './pages/admin/billing/AdminSubscriptions';
import AdminInvoices from './pages/admin/billing/AdminInvoices';
import AdminPlans from './pages/admin/billing/AdminPlans';

// NEW Premium Sales Invoice Pages
import SalesInvoicesList from './pages/SalesInvoices/SalesInvoicesList';
import SalesInvoicesListEnhanced from './pages/Sales/SalesInvoicesListEnhanced';
import SalesInvoiceDetailPage from './pages/SalesInvoices/SalesInvoiceDetailPage';
// Payments Received Module
import PaymentsReceivedPage from './pages/PaymentsReceived/PaymentsReceivedPage';
import RecordPaymentPage from './pages/PaymentsReceived/RecordPaymentPage';
import PaymentDetailPage from './pages/PaymentsReceived/PaymentDetailPage';
import CreateSalesInvoice from './pages/SalesInvoices/CreateSalesInvoice';
// Purchase Invoice Module (NEW Premium)
import PurchaseInvoicesPage from './pages/PurchaseInvoicesNew/PurchaseInvoicesPage';
import CreatePurchaseInvoicePage from './pages/PurchaseInvoicesNew/CreatePurchaseInvoicePage';
import PurchaseInvoiceDetailPage from './pages/PurchaseInvoicesNew/PurchaseInvoiceDetailPage';

// Purchase Orders
import PurchaseOrdersPage from './pages/PurchaseOrders/PurchaseOrdersPage';
import CreatePurchaseOrderPage from './pages/PurchaseOrders/CreatePurchaseOrderPage';
import PurchaseOrderDetailPage from './pages/PurchaseOrders/PurchaseOrderDetailPage';

// Purchase Receives
import PurchaseReceivesPage from './pages/PurchaseReceives/PurchaseReceivesPage';
import CreatePurchaseReceivePage from './pages/PurchaseReceives/CreatePurchaseReceivePage';
import PurchaseReceiveDetailPage from './pages/PurchaseReceives/PurchaseReceiveDetailPage';
// Payments Made Module (NEW)
import PaymentsMadePage from './pages/PaymentsMade/PaymentsMadePage';
import RecordPaymentMadePage from './pages/PaymentsMade/RecordPaymentMadePage';
import PaymentMadeDetailPage from './pages/PaymentsMade/PaymentMadeDetailPage';
// Legacy Purchase Invoices (keeping for backward compatibility)
import PurchaseInvoicesList from './pages/PurchaseInvoices/PurchaseInvoicesList';
import CreatePurchaseInvoice from './pages/PurchaseInvoices/CreatePurchaseInvoice';
import FundsAccountsPage from './pages/CashBank/FundsAccountsPage';
// Premium Expenses Module
import ExpensesPage from './pages/Expenses/ExpensesPage';
import RecordExpensePage from './pages/Expenses/RecordExpensePage';
import ExpenseDetailPage from './pages/Expenses/ExpenseDetailPage';
import MileageExpensePage from './pages/Expenses/MileageExpensePage';
import BulkAddExpensesPage from './pages/Expenses/BulkAddExpensesPage';
import ImportExpensesPage from './pages/Expenses/ImportExpensesPage';
import StaffList from './pages/Staff/StaffList';
import StaffAssignments from './pages/Staff/StaffAssignments';
import PayrollPage from './pages/Staff/PayrollPage';
import POSBillingPage from './pages/POS/POSBillingPage';
import POSBillsPage from './pages/POS/POSBillsPage';
import POSDailyReportPage from './pages/POS/POSDailyReportPage';
import POSRefundsPage from './pages/POS/POSRefundsPage';
import POSSettingsPage from './pages/POS/POSSettingsPage';
import POSMultiScreenPage from './pages/POS/POSMultiScreenPage';
import POSCashDrawerPage from './pages/POS/POSCashDrawerPage';
import DebitNotesPage from './pages/DebitNotes/DebitNotesPage';
import CreateDebitNotePage from './pages/DebitNotes/CreateDebitNotePage';
import DebitNoteDetailPage from './pages/DebitNotes/DebitNoteDetailPage';
import RecurringInvoicesPage from './pages/RecurringInvoices/RecurringInvoicesPage';
import SalesReturnsListPage from './pages/SalesReturns/SalesReturnsListPage';
import CreateSalesReturnPage from './pages/SalesReturns/CreateSalesReturnPage';
import SalesReturnDetailPage from './pages/SalesReturns/SalesReturnDetailPage';
import ReportsDashboard from './pages/Reports/ReportsDashboard';
import ExportHistory from './pages/Reports/ExportHistory';

// GST Module Pages
import GSTR1Page from './pages/GST/GSTR1Page';
import GSTR3BPage from './pages/GST/GSTR3BPage';
import SalesRegisterPage from './pages/GST/SalesRegisterPage';
import PurchaseRegisterPage from './pages/GST/PurchaseRegisterPage';
import GSTSummaryPage from './pages/GST/GSTSummaryPage';
import HSNSummaryPage from './pages/GST/HSNSummaryPage';
import GSTR2BPage from './pages/GST/GSTR2BPage';
import ITCReconciliationPage from './pages/GST/ITCReconciliationPage';
import BooksVsGSTPage from './pages/GST/BooksVsGSTPage';
import ITCEligibilityPage from './pages/GST/ITCEligibilityPage';

import { Toaster } from './components/ui/sonner';
import { Toaster as HotToaster } from 'react-hot-toast';

import './App.css';

function App() {
  return (
    <AuthProvider>
      <BusinessProvider>
        <PermissionsProvider>
          <Toaster />
          <HotToaster position="top-right" />
          <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<RoleBasedLogin />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            
            {/* Admin Routes */}
            <Route path="/admin/login" element={<RoleBasedLogin />} />
            <Route
              path="/admin/*"
              element={
                <RequireAdmin>
                  <ResponsiveAdminLayout />
                </RequireAdmin>
              }
            >
              {/* Dashboard */}
              <Route path="dashboard" element={<AdminDashboard />} />
              
              {/* Businesses */}
              <Route path="businesses" element={<Businesses />} />
              <Route path="businesses/active" element={<BusinessesActive />} />
              <Route path="businesses/inactive" element={<BusinessesInactive />} />
              <Route path="businesses/:id" element={<BusinessDetails />} />
              
              {/* Users */}
              <Route path="users" element={<Users />} />
              <Route path="users/:id" element={<UserDetails />} />
              
              {/* Plans & Billing */}
              <Route path="plans" element={<Plans />} />
              <Route path="plans-v2" element={<PlansManagementV2 />} />
              <Route path="menu-editor" element={<IndustryMenuEditor />} />
              <Route path="subscriptions" element={<Subscriptions />} />
              <Route path="invoices" element={<Invoices />} />
              
              {/* Part 5B - Admin Billing Routes */}
              <Route path="billing" element={<AdminBillingOverview />} />
              <Route path="billing/subscriptions" element={<AdminSubscriptions />} />
              <Route path="billing/invoices" element={<AdminInvoices />} />
              <Route path="billing/plans" element={<AdminPlans />} />
              
              {/* System Configuration */}
              <Route path="config/features" element={<FeatureFlags />} />
              <Route path="config/limits" element={<BusinessLimits />} />
              <Route path="config/integrations" element={<Integrations />} />
              <Route path="config/providers" element={<Providers />} />
              
              {/* Monitoring & Logs */}
              <Route path="logs" element={<SystemLogs />} />
              <Route path="logs/errors" element={<ErrorTracker />} />
              <Route path="logs/audit" element={<AuditLogs />} />
              <Route path="audit-logs" element={<AuditLogs />} />
              <Route path="health" element={<ServerHealth />} />
              <Route path="settings" element={<SystemConfig />} />
              <Route path="help" element={<PlaceholderPage />} />
              
              {/* Support Tools */}
              <Route path="support/tickets" element={<SupportTickets />} />
              <Route path="support/feedback" element={<Feedback />} />
              <Route path="support/announcements" element={<Announcements />} />
              
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
            </Route>

            {/* Business Selection */}
            <Route
              path="/select-business"
              element={
                <ProtectedRoute>
                  <BusinessSelector />
                </ProtectedRoute>
              }
            />

            {/* Protected Business Routes */}
            <Route
              path="/business/:businessId/*"
              element={
                <ProtectedRoute>
                  <ResponsiveAppShell>
                    <Routes>
                      {/* Role-specific dashboards - Allow to render, they handle auth internally */}
                      <Route path="dashboard/owner" element={<OwnerDashboard />} />
                      <Route path="dashboard/staff" element={<StaffDashboard />} />
                      
                      {/* Generic dashboard route - redirects based on role */}
                      <Route path="dashboard" element={<RoleBasedDashboardRedirect />} />
                      
                      {/* Catalogue - OWNER ONLY */}
                      <Route path="customers" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <CustomersPage />
                        </RequireRole>
                      } />
                      <Route path="customers/:customerId" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <CustomerDetailPage />
                        </RequireRole>
                      } />
                      <Route path="suppliers" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <SuppliersPage />
                        </RequireRole>
                      } />
                      <Route path="suppliers/:supplierId" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <SupplierDetailPage />
                        </RequireRole>
                      } />
                      <Route path="parties" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <PartiesList />
                        </RequireRole>
                      } />
                      <Route path="items" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <ItemsList />
                        </RequireRole>
                      } />
                      <Route path="items/custom-fields" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <CustomFieldsManager />
                        </RequireRole>
                      } />
                      
                      {/* Sales - OWNER and STAFF */}
                      <Route path="sales/invoices" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <SalesInvoicesList />
                        </RequireRole>
                      } />
                      <Route path="sales/invoices/new" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <CreateSalesInvoice />
                        </RequireRole>
                      } />
                      <Route path="sales/invoices/:id" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <SalesInvoiceDetailPage />
                        </RequireRole>
                      } />
                      <Route path="sales/invoices/:id/edit" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <CreateSalesInvoice />
                        </RequireRole>
                      } />
                      <Route path="sales/quotations" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <QuotationsList />
                        </RequireRole>
                      } />
                      <Route path="sales/quotations/new" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <CreateQuotation />
                        </RequireRole>
                      } />
                      <Route path="sales/quotations/:quotationId" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <QuotationDetailPage />
                        </RequireRole>
                      } />
                      <Route path="sales/quotations/:id/edit" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <CreateQuotation />
                        </RequireRole>
                      } />
                      <Route path="sales/quotations/:id/print" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <QuotationPrintPreview />
                        </RequireRole>
                      } />
                      <Route path="sales/quotations/:id/preview" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <QuotationPrintPreview />
                        </RequireRole>
                      } />
                      <Route path="sales/payment-in" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <PaymentsReceivedPage />
                        </RequireRole>
                      } />
                      
                      {/* Payments Received Module - OWNER and STAFF */}
                      <Route path="payments-received" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <PaymentsReceivedPage />
                        </RequireRole>
                      } />
                      <Route path="payments-received/new" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <RecordPaymentPage />
                        </RequireRole>
                      } />
                      <Route path="payments-received/:id" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <PaymentDetailPage />
                        </RequireRole>
                      } />
                      <Route path="payments-received/:id/edit" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <RecordPaymentPage />
                        </RequireRole>
                      } />
                      
                      {/* Purchases - OWNER ONLY */}
                      {/* NEW Premium Purchase Invoice Routes */}
                      <Route path="purchase-invoices" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <PurchaseInvoicesPage />
                        </RequireRole>
                      } />
                      <Route path="purchase-invoices/new" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <CreatePurchaseInvoicePage />
                        </RequireRole>
                      } />
                      <Route path="purchase-invoices/:id" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <PurchaseInvoiceDetailPage />
                        </RequireRole>
                      } />
                      <Route path="purchase-invoices/:id/edit" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <CreatePurchaseInvoicePage />
                        </RequireRole>
                      } />
                      
                      {/* Purchase Orders */}
                      <Route path="purchase-orders" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <PurchaseOrdersPage />
                        </RequireRole>
                      } />
                      <Route path="purchase-orders/new" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <CreatePurchaseOrderPage />
                        </RequireRole>
                      } />
                      <Route path="purchase-orders/:orderId" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <PurchaseOrderDetailPage />
                        </RequireRole>
                      } />
                      <Route path="purchase-orders/:orderId/edit" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <CreatePurchaseOrderPage />
                        </RequireRole>
                      } />
                      
                      {/* Purchase Receives */}
                      <Route path="purchase-receives" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <PurchaseReceivesPage />
                        </RequireRole>
                      } />
                      <Route path="purchase-receives/new" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <CreatePurchaseReceivePage />
                        </RequireRole>
                      } />
                      <Route path="purchase-receives/:receiveId" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <PurchaseReceiveDetailPage />
                        </RequireRole>
                      } />
                      
                      {/* NEW Payments Made Module */}
                      <Route path="payments-made" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <PaymentsMadePage />
                        </RequireRole>
                      } />
                      <Route path="payments-made/new" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <RecordPaymentMadePage />
                        </RequireRole>
                      } />
                      <Route path="payments-made/:id" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <PaymentMadeDetailPage />
                        </RequireRole>
                      } />
                      <Route path="payments-made/:id/edit" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <RecordPaymentMadePage />
                        </RequireRole>
                      } />
                      
                      {/* Legacy Purchase Invoice Routes (keeping for backward compatibility) */}
                      <Route path="purchases/invoices" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <PurchaseInvoicesList />
                        </RequireRole>
                      } />
                      <Route path="purchases/invoices/create" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <CreatePurchaseInvoice />
                        </RequireRole>
                      } />
                      <Route path="purchases/invoices/:id/edit" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <CreatePurchaseInvoice />
                        </RequireRole>
                      } />
                      <Route path="purchases/payment-out" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <PaymentsMadePage />
                        </RequireRole>
                      } />
                      
                      {/* Sales Returns - OWNER and STAFF */}
                      <Route path="sales-returns" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <SalesReturnsListPage />
                        </RequireRole>
                      } />
                      <Route path="sales-returns/new" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <CreateSalesReturnPage />
                        </RequireRole>
                      } />
                      <Route path="sales-returns/:returnId" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <SalesReturnDetailPage />
                        </RequireRole>
                      } />
                      <Route path="sales-returns/:returnId/edit" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <CreateSalesReturnPage />
                        </RequireRole>
                      } />
                      
                      {/* Funds & Accounts - OWNER full access, STAFF read-only */}
                      <Route path="cash-bank" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <FundsAccountsPage />
                        </RequireRole>
                      } />
                      <Route path="funds-accounts" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <FundsAccountsPage />
                        </RequireRole>
                      } />
                      
                      {/* Expenses - OWNER and STAFF */}
                      <Route path="expenses" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <ExpensesPage />
                        </RequireRole>
                      } />
                      <Route path="expenses/new" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <RecordExpensePage />
                        </RequireRole>
                      } />
                      <Route path="expenses/:id" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <ExpenseDetailPage />
                        </RequireRole>
                      } />
                      <Route path="expenses/:id/edit" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <RecordExpensePage />
                        </RequireRole>
                      } />
                      <Route path="expenses/mileage" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <MileageExpensePage />
                        </RequireRole>
                      } />
                      <Route path="expenses/bulk" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <BulkAddExpensesPage />
                        </RequireRole>
                      } />
                      <Route path="expenses/import" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <ImportExpensesPage />
                        </RequireRole>
                      } />
                      
                      {/* Staff & Payroll - OWNER and ADMIN */}
                      <Route path="staff" element={
                        <RequireRole allowedRoles={['OWNER', 'ADMIN']}>
                          <StaffList />
                        </RequireRole>
                      } />
                      <Route path="staff/assignments" element={
                        <RequireRole allowedRoles={['OWNER', 'ADMIN']}>
                          <StaffAssignments />
                        </RequireRole>
                      } />
                      <Route path="payroll" element={
                        <RequireRole allowedRoles={['OWNER', 'ADMIN']}>
                          <PayrollPage />
                        </RequireRole>
                      } />
                      
                      {/* POS - OWNER and STAFF */}
                      <Route path="pos" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <POSBillingPage />
                        </RequireRole>
                      } />
                      <Route path="pos/bills" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <POSBillsPage />
                        </RequireRole>
                      } />
                      <Route path="pos/daily-report" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <POSDailyReportPage />
                        </RequireRole>
                      } />
                      <Route path="pos/refunds" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <POSRefundsPage />
                        </RequireRole>
                      } />
                      <Route path="pos/settings" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <POSSettingsPage />
                        </RequireRole>
                      } />
                      <Route path="pos/multi-screen" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <POSMultiScreenPage />
                        </RequireRole>
                      } />
                      <Route path="pos/cash-drawer" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <POSCashDrawerPage />
                        </RequireRole>
                      } />
                      
                      {/* Debit Notes */}
                      <Route path="debit-notes" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <DebitNotesPage />
                        </RequireRole>
                      } />
                      <Route path="debit-notes/new" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <CreateDebitNotePage />
                        </RequireRole>
                      } />
                      <Route path="debit-notes/:noteId" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <DebitNoteDetailPage />
                        </RequireRole>
                      } />
                      <Route path="debit-notes/:noteId/edit" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <CreateDebitNotePage />
                        </RequireRole>
                      } />
                      
                      {/* Recurring Invoices */}
                      <Route path="recurring-invoices" element={<RecurringInvoicesPage />} />
                      
                      {/* Reports - OWNER and STAFF (read-only for staff) */}
                      <Route path="reports" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <ReportsDashboard />
                        </RequireRole>
                      } />
                      <Route path="reports/export-history" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <ExportHistory />
                        </RequireRole>
                      } />
                      
                      {/* GST Module - OWNER ONLY */}
                      <Route path="gst/gstr1" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <GSTR1Page />
                        </RequireRole>
                      } />
                      <Route path="gst/gstr3b" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <GSTR3BPage />
                        </RequireRole>
                      } />
                      <Route path="gst/sales-register" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <SalesRegisterPage />
                        </RequireRole>
                      } />
                      <Route path="gst/purchase-register" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <PurchaseRegisterPage />
                        </RequireRole>
                      } />
                      <Route path="gst/summary" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <GSTSummaryPage />
                        </RequireRole>
                      } />
                      <Route path="gst/hsn-summary" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <HSNSummaryPage />
                        </RequireRole>
                      } />
                      <Route path="gst/gstr2b" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <GSTR2BPage />
                        </RequireRole>
                      } />
                      <Route path="gst/itc-reconciliation" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <ITCReconciliationPage />
                        </RequireRole>
                      } />
                      <Route path="gst/books-vs-gst" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <BooksVsGSTPage />
                        </RequireRole>
                      } />
                      <Route path="gst/itc-eligibility" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <ITCEligibilityPage />
                        </RequireRole>
                      } />
                      
                      {/* Part 3: Tracking (Owner) */}
                      <Route path="tracking/live" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <LiveTrackingPage />
                        </RequireRole>
                      } />
                      <Route path="tracking/routes" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <StaffRoutesPage />
                        </RequireRole>
                      } />
                      <Route path="tracking/visits" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <VisitsTasksPage />
                        </RequireRole>
                      } />
                      <Route path="tracking/alerts" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <TrackingAlertsPage />
                        </RequireRole>
                      } />
                      <Route path="tracking/reports" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <TrackingReportsPage />
                        </RequireRole>
                      } />
                      
                      {/* Part 3: Tracking (Staff) */}
                      <Route path="tracking/my-shift" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <MyShiftPage />
                        </RequireRole>
                      } />
                      <Route path="tracking/my-visits" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <MyVisitsPage />
                        </RequireRole>
                      } />
                      <Route path="tracking/my-retailers" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <MyRetailersPage />
                        </RequireRole>
                      } />
                      
                      {/* NEW Staff-Only Pages */}
                      <Route path="staff/my-attendance" element={
                        <RequireRole allowedRoles={['STAFF']}>
                          <MyAttendancePage />
                        </RequireRole>
                      } />
                      <Route path="tracking/my-routes" element={
                        <RequireRole allowedRoles={['STAFF']}>
                          <MyRoutesPage />
                        </RequireRole>
                      } />
                      <Route path="staff/my-profile" element={
                        <RequireRole allowedRoles={['STAFF']}>
                          <MyProfilePage />
                        </RequireRole>
                      } />
                      <Route path="finance/my-collections" element={
                        <RequireRole allowedRoles={['STAFF']}>
                          <MyCollectionsPage />
                        </RequireRole>
                      } />
                      <Route path="tracking/my-retailers-map" element={
                        <RequireRole allowedRoles={['STAFF']}>
                          <MyRetailersMapPage />
                        </RequireRole>
                      } />
                      <Route path="staff/record-placement" element={
                        <RequireRole allowedRoles={['STAFF']}>
                          <RecordPlacementPage />
                        </RequireRole>
                      } />
                      
                      {/* Part 4: Collections (Owner) */}
                      <Route path="tracking/staff/:staffId/collections" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <StaffCollectionsProfilePage />
                        </RequireRole>
                      } />
                      
                      {/* Part 5: Retailer Profile (Owner) */}
                      <Route path="tracking/retailers/:retailerId/profile" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <RetailerProfilePage />
                        </RequireRole>
                      } />
                      
                      {/* Part 5A: Intelligence & Automations (Owner) */}
                      <Route path="intelligence/overview" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <OwnerIntelligenceDashboard />
                        </RequireRole>
                      } />
                      <Route path="intelligence/staff" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <StaffInsightsPage />
                        </RequireRole>
                      } />
                      <Route path="intelligence/staff/:staffId" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <StaffDetailPage />
                        </RequireRole>
                      } />
                      <Route path="intelligence/retailers-risk" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <RetailerRiskPage />
                        </RequireRole>
                      } />
                      <Route path="intelligence/alerts" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <AlertsCenterPage />
                        </RequireRole>
                      } />
                      
                      {/* Part 5A: Intelligence (Staff) */}
                      <Route path="intelligence/my-performance" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <StaffMyPerformancePage />
                        </RequireRole>
                      } />
                      <Route path="intelligence/my-alerts" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <MyAlertsPage />
                        </RequireRole>
                      } />
                      
                      {/* Part 5B: Billing - OWNER ONLY */}
                      <Route path="billing" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <BillingDashboard />
                        </RequireRole>
                      } />
                      <Route path="billing/plans" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <PlansPage />
                        </RequireRole>
                      } />
                      <Route path="billing/invoices" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <InvoicesPage />
                        </RequireRole>
                      } />
                      
                      {/* Settings - OWNER ONLY */}
                      <Route path="settings" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <BusinessSettingsPage />
                        </RequireRole>
                      } />
                      <Route path="settings/users-roles" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <UsersRolesPage />
                        </RequireRole>
                      } />
                      <Route path="settings/print-template" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <PrintTemplateSettings />
                        </RequireRole>
                      } />
                      <Route path="settings/templates" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <TemplateStudioPage />
                        </RequireRole>
                      } />
                      <Route path="settings/change-password" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <ChangePasswordPage />
                        </RequireRole>
                      } />
                      <Route path="settings/security" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <MasterPasswordPage />
                        </RequireRole>
                      } />

                      {/* Sales Orders - OWNER and STAFF */}
                      <Route path="sales-orders" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <SalesOrdersPage />
                        </RequireRole>
                      } />
                      <Route path="sales-orders/new" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <CreateSalesOrderPage />
                        </RequireRole>
                      } />
                      <Route path="sales-orders/:orderId" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <SalesOrderDetailPage />
                        </RequireRole>
                      } />
                      <Route path="sales-orders/:orderId/edit" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <CreateSalesOrderPage />
                        </RequireRole>
                      } />
                      
                      {/* Proforma Invoices - OWNER and STAFF */}
                      <Route path="proforma-invoices" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <ProformaInvoicesPage />
                        </RequireRole>
                      } />
                      <Route path="proforma-invoices/new" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <CreateProformaInvoice />
                        </RequireRole>
                      } />
                      <Route path="proforma-invoices/:id" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <ProformaInvoiceDetailPage />
                        </RequireRole>
                      } />
                      <Route path="proforma-invoices/:id/edit" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <CreateProformaInvoice />
                        </RequireRole>
                      } />
                      
                      {/* Delivery Challans - OWNER and STAFF */}
                      <Route path="delivery-challans" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <DeliveryChallansPage />
                        </RequireRole>
                      } />
                      <Route path="delivery-challans/new" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <CreateDeliveryChallanPage />
                        </RequireRole>
                      } />
                      <Route path="delivery-challans/:challanId" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <DeliveryChallanDetailPage />
                        </RequireRole>
                      } />
                      <Route path="delivery-challans/:challanId/edit" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <CreateDeliveryChallanPage />
                        </RequireRole>
                      } />
                      
                      {/* Sales Returns - OWNER and STAFF */}
                      <Route path="sales-returns" element={
                        <RequireRole allowedRoles={['OWNER', 'STAFF']}>
                          <SalesReturnsPage />
                        </RequireRole>
                      } />
                      
                      {/* Other Settings */}
                      <Route path="pricing" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <Placeholder title="Pricing & Plans" description="Manage your subscription" />
                        </RequireRole>
                      } />
                      <Route path="manage-users" element={
                        <RequireRole allowedRoles={['OWNER']}>
                          <Placeholder title="Manage Users" description="Invite and manage team members" />
                        </RequireRole>
                      } />
                      
                      {/* Default redirect - use RoleBasedDashboardRedirect */}
                      <Route path="*" element={<RoleBasedDashboardRedirect />} />
                    </Routes>
                  </ResponsiveAppShell>
                </ProtectedRoute>
              }
            />

            {/* Default redirect */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
        </PermissionsProvider>
      </BusinessProvider>
    </AuthProvider>
  );
}

export default App;
